// src/encounter/lib/to-occupation-request.ts
// Build the PUT body for the rail's Occupation card (PROD-1272). Occupation is a SINGLE entity per
// patient (PatientOccupation) edited in place — Flutter does `PUT {Patients}/PatientOccupation/{id}`
// with `jsonEncode(request)` (patient_data_source_impl.dart:467), i.e. the whole entity is the body.
// Pure: transport + cache live in use-save-occupation.ts. There is no add-row/delete model — "add"
// vs "edit" is just whether the entity already has values (Flutter isValidData), so this always
// produces an upsert and round-trips the existing entity `id`.
import type { PatientOccupation } from '@/types/patient-detail-api';

/** The dialog's resolved free-text fields (mirrors occupation_form.dart). */
export interface OccupationFormValue {
  jobRole: string;
  employment: string;
  occupationalInformation: string;
}

export interface OccupationRequest {
  patientId: string;
  body: PatientOccupation;
}

/**
 * Resolve the form value into a PUT upsert body. Round-trips the existing entity `id` (so an edit
 * updates in place rather than orphaning a row). Returns null when every field is blank — nothing to
 * save (mirrors PatientOccupation.isValidData: invalid unless employment | jobRole | info present).
 */
export function buildOccupationRequest(
  value: OccupationFormValue,
  ctx: { patientId: string; existing?: PatientOccupation | null },
): OccupationRequest | null {
  const jobRole = value.jobRole.trim();
  const employment = value.employment.trim();
  const occupationalInformation = value.occupationalInformation.trim();
  if (!jobRole && !employment && !occupationalInformation) return null;
  return {
    patientId: ctx.patientId,
    body: {
      id: ctx.existing?.id ?? null,
      patientId: ctx.patientId,
      employment: employment || null,
      jobRole: jobRole || null,
      occupationalInformation: occupationalInformation || null,
    },
  };
}
